import clsx from "clsx"
import { useTranslation } from "next-i18next"
import Button from "../../comps/button"
import { CustomThemedResource } from "../../comps/image"
import {
	ChatRequest,
	MediasFullType,
} from "../../controllers/dashboard"
import { assetUrl } from "../../utils/cdn"
import { SelectedMediaType } from "./edit-segments-popup"
import { EditSegmentsAction } from "./response-message"

interface EditSegmentsHeaderProps {
	setSelected: (v: EditSegmentsAction) => void
	selected: EditSegmentsAction
	closePopup: () => void
	hasWebsite: boolean
}

export function EditSegmentsHeader(
	props: EditSegmentsHeaderProps,
) {
	const { selected, setSelected, closePopup, hasWebsite } =
		props
	const { t } = useTranslation()

	const header: {
		name: string
		value: EditSegmentsAction
	}[] = hasWebsite
		? [
				{ name: "lbl_media", value: "lbl_upload" },
				{
					name: "txt_stock_media",
					value: "txt_stock_media",
				},
				{
					name: "lbl_website",
					value: "txt_website_images",
				},
		  ]
		: [
				{ name: "lbl_media", value: "lbl_upload" },
				{
					name: "txt_stock_media",
					value: "txt_stock_media",
				},
		  ]

	return (
		<div
			className={clsx(
				"flex h-[50px] w-full items-center justify-between px-[12px]",
				"min-h-[50px] border-b border-color-separator",
			)}>
			<div className="flex h-full">
				{header.map((elem) => {
					return (
						<button
							key={elem.value}
							onClick={() => setSelected(elem.value)}
							className={clsx(
								selected === elem.value
									? "border-primary-600"
									: "border-color-separator",
								"flex h-full items-center justify-center border-b px-[16px]",
							)}>
							<span
								className={clsx(
									selected === elem.value
										? "text-primary-600"
										: "text-blue-800",
									"text-[16px] font-500 leading-[20px]",
								)}>
								{t(elem.name)}
							</span>
						</button>
					)
				})}
			</div>
			<button
				onClick={closePopup}
				className={clsx(
					"items-center justify-center desktop:hidden",
				)}>
				<CustomThemedResource
					format="svg"
					source="/general/close"
					className="h-[24px] w-[24px]"
				/>
			</button>
		</div>
	)
}

interface FooterProps {
	maxUploadCount: number
	currentUploadCount: number
	selectedMedias: SelectedMediaType[]
	selectedItem: number | null
	setSelectedItem: (v: number) => void
	sendSelectedMedias: (req: ChatRequest) => void
	removeSelected: (index: number) => void
	closePopup: () => void
}

export function Footer(props: FooterProps) {
	const {
		maxUploadCount,
		currentUploadCount,
		selectedMedias,
		selectedItem,
		setSelectedItem,
		sendSelectedMedias,
		removeSelected,
		closePopup,
	} = props
	const { t } = useTranslation()

	return (
		<div
			className={clsx(
				"flex h-[203px] w-full flex-col py-[10px] tablet:flex-row",
				"tablet:h-[129px]",
			)}>
			<div
				className={clsx(
					"no-scrollbar mt-[-150px] flex w-full gap-[10px] overflow-x-scroll px-[16px] pt-[150px]",
				)}>
				{selectedMedias.map((media, i) => {
					const index = i + 1
					return (
						<div
							className="group relative flex flex-col items-center gap-[4px]"
							key={index}>
							<button
								onClick={() => setSelectedItem(index)}
								className={clsx(
									selectedItem === index
										? "border-primary-600"
										: "border-color-separator",
									"h-[78px] min-w-[78px] rounded-[10px] border-[1.5px] p-[5px]",
									"relative flex max-w-[78px] items-center justify-center",
								)}>
								<EditSegmentsMediaItem {...media} />
							</button>
							<span
								className={clsx(
									selectedItem === index
										? "text-primary-600"
										: "text-blue-400",
									"text-[13px] font-600",
								)}>
								{index}
							</span>
							{media.media_type !== "empty" && (
								<button
									onClick={() => removeSelected(index)}
									className={clsx(
										"absolute right-[10px] top-[10px] h-[24px] w-[24px]",
										"rounded-full bg-color-cell hover:bg-primary-100",
										"cursor-pointer items-center justify-center",
										"hidden group-hover:flex",
									)}>
									<CustomThemedResource
										source="/general/minus-gray"
										format="svg"
										alt="minus icon"
									/>
								</button>
							)}

							<div
								className={clsx(
									i === 0
										? "left-[-10px]"
										: "left-1/2 -translate-x-1/2 ",
									"absolute group-hover:opacity-100",
									"bottom-[120px] flex max-w-[400px] transition-opacity",
									"z-40 min-w-max opacity-0 duration-300",
								)}>
								<div
									className={clsx(
										i === 0
											? "left-[45px]"
											: "left-1/2 -translate-x-1/2",
										"h-[12px] w-[12px] rotate-45 rounded-[3px] bg-color-tooltip",
										"absolute -bottom-[5px]",
									)}
								/>
								<div
									className={clsx(
										"max-w-[250px] bg-color-tooltip p-[8px] text-blue-100",
										"w-full rounded-[8px] text-[13px] font-500 leading-[14px]",
									)}>
									{media.hint}
								</div>
							</div>
						</div>
					)
				})}
			</div>

			<div
				className={clsx(
					"flex items-center justify-center px-[16px] pb-[16px] pt-[9px]",
					"tablet:w-[314px]",
				)}>
				<Button
					onClick={() => {
						closePopup()
						sendSelectedMedias({
							name: "media_source",
							medias: selectedMedias as MediasFullType,
						})
					}}
					disabled={
						selectedMedias.find(
							(m) => m.media_type === "empty" || m.loading,
						)
							? true
							: false
					}
					primary
					text={`${t(
						"lbl_send",
					)} (${currentUploadCount}/${maxUploadCount})`}
					className="w-full max-w-[343px] tablet:max-w-[282px]"
				/>
			</div>
		</div>
	)
}

export function EditSegmentsMediaItem(
	props: SelectedMediaType,
) {
	const { media_type } = props

	switch (media_type) {
		case "empty":
			return (
				<div className="aspect-1 h-full max-h-full rounded-[5px] bg-blue-200" />
			)

		case "image":
			return (
				<>
					<img
						src={
							props.resource_thumb_url ?? props.resource_url
						}
						alt="user uploaded file"
						className="h-full max-h-full w-full overflow-hidden rounded-[6px] object-cover"
						style={{
							aspectRatio: props.aspect,
						}}
					/>
					{props.loading && (
						<div
							className={clsx(
								"absolute left-0 top-0 h-full w-full",
								"flex items-center justify-center rounded-[6px] bg-color-popup",
							)}>
							<div className="animate-spin">
								<img
									src={assetUrl(
										"/general/loading-dark.webp?aa",
									)}
									alt="loading"
									className="h-[18px] w-[18px]"
								/>
							</div>
						</div>
					)}
				</>
			)

		case "video":
			return (
				<>
					{props.thumb_url === null ? (
						<video
							src={props.resource_url + "#t=0.001"}
							className="h-full max-h-full w-full overflow-hidden rounded-[6px] object-cover"
							style={{
								aspectRatio: props.aspect,
							}}
						/>
					) : (
						<img
							src={props.thumb_url}
							alt="thumbnail"
							className="h-full max-h-full w-full overflow-hidden rounded-[6px] object-cover"
							style={{
								aspectRatio: props.aspect,
							}}
						/>
					)}
					<img
						className={clsx(
							"absolute bottom-[8px] left-[8px]",
							"h-[24px] w-[24px]",
						)}
						alt="play white"
						src={assetUrl("/general/play-white.png")}
					/>
					{props.loading && (
						<div
							className={clsx(
								"absolute left-0 top-0 h-full w-full",
								"flex items-center justify-center rounded-[6px] bg-color-popup",
							)}>
							<div className="animate-spin">
								<CustomThemedResource
									format="webp"
									source="/general/loading"
									className="h-[18px] w-[18px]"
								/>
							</div>
						</div>
					)}
				</>
			)
	}
}
